import IconRender from 'components/common/IconRender'
import Modal from 'components/common/modal/Modal'
import { formatDate } from 'components/utils/FormatDate'
import { Unit } from 'newTypes/unit'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { DisplayMode } from 'types/customParams'
import { CustomColorsRef } from 'types/general'
import { VerificationStatus } from 'types/property'
import 'react-toastify/dist/ReactToastify.css'

type ShareUnitTemplatesProps = {
  isOpen: boolean
  handleClose: () => void
  unit: Unit
}

const ShareUnitTemplates = ({
  isOpen,
  handleClose,
  unit,
}: ShareUnitTemplatesProps) => {
  const [url, setUrl] = useState('')
  const [selectedOption, setSelectedOption] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!unit || typeof window === 'undefined') return

    const origin = window.location.origin
    setUrl(`${origin}/listing/${unit.id}`)
  }, [unit])

  const generateTemplate = (option: string) => {
    const baseLines = [
      `🚨 Lease Transfer Opportunity! 🚨`,
      ``,
      `Lovely unit in ${unit.address.city}`,
      `Title: "${unit.title || 'Cozy Unit - Lease Transfer'}"`,
      ``,
      `🔹 ${unit.bedrooms === 0 ? 'Studio' : `${unit.bedrooms} Bedroom${unit.bedrooms !== 1 ? 's' : ''}`} / ${unit.bathrooms} Bathroom${unit.bathrooms !== 1 ? 's' : ''}`,
      `💲 $${unit.price}/month`,
      `📍 ${unit.address.number} ${unit.address.street}, ${unit.address.city}`,
      `📅 Available: ${formatDate(unit.availability)}`,
      `📅 Lease End: ${formatDate(unit.contractEnd)}`,
      unit.size ? `📏 Size: ${unit.size} ${unit.sizeUnit}` : null,
      unit.verified === VerificationStatus.Approved
        ? `✅ Ownership Verified`
        : null,
      ``,
    ]

    const templates = {
      WhatsApp: [...baseLines, `🔗 View: ${url}`].filter(Boolean).join('\n'),
      Facebook: [...baseLines, `More info at ${url}`]
        .filter(Boolean)
        .join('\n'),
      Email: [...baseLines, `Details here: ${url}`].filter(Boolean).join('\n'),
      SMS: [`Check this out:\n${url}`].join('\n'),
    }

    return templates[option as keyof typeof templates] || ''
  }

  const handleCopy = async (text: string) => {
    setIsLoading(true)
    try {
      await navigator.clipboard.writeText(text)
      toast.success('Copied!')
    } catch {
      toast.error('Copy failed')
    } finally {
      setIsLoading(false)
    }
  }

  const shareOptions = [
    { icon: 'brands-whatsapp', label: 'WhatsApp' },
    { icon: 'brands-facebook', label: 'Facebook' },
    { icon: 'regular-envelope', label: 'Email' },
    { icon: 'regular-message', label: 'SMS' },
  ]

  if (!isOpen) return null

  const previewTemplate = selectedOption ? generateTemplate(selectedOption) : ''
  return (
    <Modal
      manualSize={DisplayMode.Modal}
      title={`Partager l'annonce`}
      isOpen={isOpen}
      onClose={handleClose}
      headerVariant="light"
      className="!w-[min(92vw,720px)] !h-auto !max-h-[85vh] rounded-2xl"
      customHeaderClasses="!items-start !px-5 !py-4"
      customBodyClasses="!bg-white"
    >
      <div className="p-4 md:p-5 space-y-5">
        <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">
            Annonce
          </p>
          <p className="text-sm font-semibold text-imovSecondary">
            {unit?.title || 'Cozy Unit - Lease Transfer'}
          </p>
        </div>

        {/* Shareable URL */}
        <div>
          <label className="text-sm font-medium text-gray-700">Lien direct</label>
          <div className="flex items-center gap-2 mt-1">
            <button
              type="button"
              onClick={() => handleCopy(url)}
              className="h-[42px] w-[46px] rounded-lg border border-gray-300 bg-white hover:bg-gray-100 flex items-center justify-center transition-colors"
            >
              <IconRender
                icon="regular-copy"
                color={CustomColorsRef.Primary}
              />
            </button>
            <input
              value={url}
              readOnly
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-sm bg-white text-gray-700"
            />
          </div>
        </div>

        {/* Share Options */}
        <div>
          <label className="text-sm font-medium text-gray-700">Plateforme</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {shareOptions.map(option => (
              <button
                key={option.label}
                onClick={() => setSelectedOption(option.label)}
                type="button"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition-colors ${
                  selectedOption === option.label
                    ? 'border-imovPrimary bg-imovPrimary/10 text-imovPrimary'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                <IconRender icon={option.icon} />
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Template Preview */}
        {selectedOption && (
          <div>
            <label className="text-sm font-medium text-gray-700">Aperçu: {selectedOption}</label>
            <pre className="mt-1 bg-gray-50 border border-gray-200 rounded-lg p-3 whitespace-pre-wrap text-sm max-h-52 overflow-auto text-gray-700">
              {previewTemplate}
            </pre>
            <button
              type="button"
              onClick={() => handleCopy(previewTemplate)}
              disabled={isLoading || !previewTemplate}
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-imovPrimary bg-imovPrimary text-white hover:bg-imovPrimaryDark disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              <IconRender icon="regular-copy" color={CustomColorsRef.White} />
              {isLoading ? 'Copie en cours...' : 'Copier le template'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default ShareUnitTemplates
